import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User as UserIcon } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import type { MembershipTier } from '../../../types/auth';

/**
 * 账户中心弹窗 —— 按 Figma 168-223「个人信息」与 169-32「水印设置」两页 1:1 还原，
 * 共用同一弹窗骨架，左侧菜单可在两个已实现页面间切换
 *
 * 骨架（800x586，画布内 x=560,y=247）：
 *   遮罩  全屏 #121212 blur(8px) 65% (#168:224)
 *   弹窗  800x586 / #1D1D1D / 边框 1px #2D2D2D / 圆角 16 (#168:240)
 *   左菜单 168 宽 / #222222 / 左圆角 16：
 *     「账户中心」标题 + 「我的」分组（个人信息[选中 #2D2D2D 152x40 圆角8]/积分账单/
 *     优惠券/我的作品/我的收藏/我的点赞）+「系统」分组（订阅与发票/水印设置/通知）+
 *     退出登录（#F53F3F 65%）
 *   右内容（个人信息页）：用户卡 410x64（头像64 / 昵称 #8BCDFF 18px + 编辑 / 手机 + UID + 复制）+
 *     会员卡（设计稿给出未开通 #168:718 与已开通 #168:719 两种状态卡，按会员状态二选一）
 *   右内容（水印设置页 169-32）：规则卡 584x412（管理规则正文 / 「去AI水印」开关 / 确认说明）
 *
 * 层级：z-[250]，盖过顶栏（200）与顶栏下拉（150），低于登录弹窗（300）。
 * 布局：整页缩放体系已拆除（即梦式流式布局），弹窗按设计稿原始 800x586 常规渲染；
 *   createPortal 到 body 渲染 —— 独立于顶栏/侧边栏层叠上下文，层级清晰可控。
 */

/** 已实现的页签：其余菜单项（积分账单/优惠券等）设计稿尚未交付，点击暂不响应 */
type AccountTab = 'profile' | 'watermark';

/** 规则正文 #169:151：12px Light / 行高 24 / 两端对齐，两段以换行分隔 */
const WATERMARK_RULES_TEXT =
  '根据法律法规要求，Bong AI平台（"平台"）提供AI生成合成服务，可能导致公众混淆或者误认的，平台应当在AI生成合成内容上添加显式标识（即"AI生成"明水印），向公众进行提示。经过您的申请，平台可以向您提供未添加显式标识的AI生成合成内容。如您后续使用网络信息内容传播服务发布AI生成合成内容，请注意您还需主动声明并使用传播平台提供的标识功能进行标识。您理解并承诺，如您未按照法律法规要求在AI生成合成内容上添加显式标识，导致公众混淆或者误认，因此所发生的后果和责任均由您自行承担。\n当您开启"去除水印"功能开关后，您通过本账号创作、生成的AI生成合成内容，将不再添加"AI生成"明水印。';

/** 菜单项通用结构：40 高行 / 圆角 8 / icon 16 (x=24) + 文字 14px (x=48)，与设计稿一致 */
interface MenuItemDef {
  key: string;
  label: string;
  icon: string;
}

const MY_GROUP: MenuItemDef[] = [
  { key: 'profile', label: '个人信息', icon: '/icons/user-menu/profile.svg' },
  { key: 'points-bill', label: '积分账单', icon: '/icons/user-center/points-bill.svg' },
  { key: 'coupon', label: '优惠券', icon: '/icons/user-center/coupon.svg' },
  { key: 'my-works', label: '我的作品', icon: '/icons/user-center/my-works.svg' },
  { key: 'my-favorites', label: '我的收藏', icon: '/icons/user-center/my-favorites.svg' },
  { key: 'my-likes', label: '我的点赞', icon: '/icons/user-center/my-likes.svg' },
];

const SYSTEM_GROUP: MenuItemDef[] = [
  { key: 'subscription', label: '订阅与发票', icon: '/icons/user-center/subscription-invoice.svg' },
  { key: 'watermark', label: '水印设置', icon: '/icons/user-menu/watermark.svg' },
  { key: 'notification', label: '通知', icon: '/icons/user-menu/bell.svg' },
];

/** 左侧菜单项：40 高行 / 选中 #2D2D2D；已实现页签可点击切换，其余项保留 hover 观感 */
const MenuItem: React.FC<{ item: MenuItemDef; active?: boolean; onClick?: () => void }> = ({ item, active = false, onClick }) => (
  <div
    onClick={onClick}
    className={`mx-2 flex h-10 shrink-0 items-center rounded-lg ${active ? '' : 'cursor-pointer hover:bg-white/5 transition-colors'}`}
    style={active ? { backgroundColor: '#2D2D2D' } : undefined}
    aria-current={active ? 'page' : undefined}
  >
    <img src={item.icon} alt="" width={16} height={16} className="ml-4 shrink-0" />
    <span
      className="ml-2 text-[14px] leading-[14px]"
      style={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.65)' }}
    >
      {item.label}
    </span>
  </div>
);

/** 分组标签：「我的」/「系统」 12px 45% 白 */
const GroupLabel: React.FC<{ text: string }> = ({ text }) => (
  <div className="mx-4 mb-2 mt-6 shrink-0 text-[12px] leading-[12px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
    {text}
  </div>
);

/** 会员卡积分行：icon 12 + 积分数 14px + 分隔线 +「可用积分额度」+ 右侧「积分充值」+ 右箭头
    （未开通卡 #168:718 / 已开通卡 #168:719 首行同构，仅数值不同） */
const PointsRow: React.FC<{ value: number }> = ({ value }) => (
  <div className="flex h-[52px] items-center pl-6 pr-6">
    <img src="/icons/user-center/token-icon.svg" alt="" width={12} height={12} className="shrink-0" />
    <span className="ml-1 text-[14px] font-medium leading-[14px] text-white">{value}</span>
    <span className="mx-3 h-[10px] w-px shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
    <span className="text-[12px] leading-[12px]" style={{ color: 'rgba(255,255,255,0.45)' }}>可用积分额度</span>
    <button className="ml-auto flex items-center gap-1 transition-opacity hover:opacity-80">
      <span className="text-[12px] leading-[12px]" style={{ color: 'rgba(255,255,255,0.65)' }}>积分充值</span>
      <img src="/icons/user-center/chevron-right.svg" alt="" width={16} height={16} />
    </button>
  </div>
);

/** 未开通会员卡 #168:718：584x112 / 白 2% / 圆角 8，积分行 + 虚线「立即开通会员」 */
const InactiveMemberCard: React.FC<{ points: number }> = ({ points }) => (
  <div className="h-[112px] w-[584px] rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
    <PointsRow value={points} />
    {/* 虚线开通按钮 #168:714：536x44 / 边框 1px dashed #363636 (2,2) / 圆角 8 / 14px Medium 居中 */}
    <button
      className="mx-6 flex h-11 w-[536px] items-center justify-center rounded-lg text-[14px] font-medium text-white transition-colors hover:bg-white/5"
      style={{ border: '1px dashed #363636' }}
    >
      立即开通会员
    </button>
  </div>
);

/** 档位简名（会员卡「个人版 / XX」用，与订阅页卡片标题同口径） */
const TIER_SHORT_NAME: Partial<Record<MembershipTier, string>> = {
  monthly: '月卡',
  quarterly: '季卡',
  yearly: '年卡',
  yearlyPro: '年卡PRO',
};

/** 已开通会员卡 #168:719：584x180 / 白 2% / 圆角 8，积分行 + 金色渐变 VIP 卡 #168:772。
    expiresAt = 订阅到期日 / periodExpiresAt = 当期重置日（均 ISO，展示截取年月日）；
    periodCredits = 当期套餐积分额度（me.currentPeriodCredits）；
    进度条填充 = 余额 / 当期额度（总余额近似当期剩余口径，满格封顶） */
const ActiveMemberCard: React.FC<{
  points: number;
  expiresAt?: string | null;
  tierName?: string;
  periodCredits?: number;
  periodExpiresAt?: string | null;
}> = ({ points, expiresAt, tierName, periodCredits, periodExpiresAt }) => {
  // 进度条填充宽度：余额占当期额度比例 × 轨道 504px，满格封顶（额度缺省时不填充）
  const fillWidth = periodCredits && periodCredits > 0
    ? Math.min(504, Math.round((points / periodCredits) * 504))
    : 0;
  return (
  <div className="relative h-[180px] w-[584px] rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
    <PointsRow value={points} />
    {/* VIP 卡：536x94 / 渐变 225deg / 边框 rgba(255,198,93,0.15) / 圆角 12 */}
    <div
      className="absolute left-6 top-[62px] h-[94px] w-[536px] rounded-xl"
      style={{
        background: 'linear-gradient(225deg, rgba(71,46,0,0.45) 0%, rgba(22,14,0,0.45) 100%)',
        border: '1px solid rgba(255,198,93,0.15)',
      }}
    >
      {/* 套餐行：「个人版 / {档位}」+ 订阅到期日（后端 subscriptionExpiresAt，截取年月日） */}
      <div className="absolute left-4 top-4 flex items-baseline">
        <span className="text-[14px] leading-[14px] text-white">个人版{tierName ? ` / ${tierName}` : ''}</span>
        <span className="ml-3 text-[14px] font-light leading-[14px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
          {expiresAt ? `${expiresAt.slice(0, 10)}到期` : ''}
        </span>
      </div>
      {/* 升级套餐徽章 #169:3/#168:778：64x20 / rgba(217,217,217,0.15) / 圆角 4 */}
      <button
        className="absolute right-4 top-[13px] flex h-5 w-16 items-center justify-center rounded transition-opacity hover:opacity-80"
        style={{ backgroundColor: 'rgba(217,217,217,0.15)' }}
      >
        <span className="text-[12px] font-medium leading-[12px] text-white">升级套餐</span>
      </button>
      {/* 会员积分进度条 #168:774/#168:775：轨道 504x4 白 25% / 填充 #FFEFD2 */}
      <div className="absolute left-4 top-[46px] h-1 w-[504px] rounded-sm" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}>
        <div className="h-1 rounded-sm" style={{ backgroundColor: '#FFEFD2', width: fillWidth }} />
      </div>
      {/* 额度说明行 #168:777/#168:789：「套餐内积分额度：{额度}」+ 竖线白25% + 「{yyyy/MM/dd} 重置」 */}
      <div className="absolute left-4 top-[66px] flex items-center text-[12px] leading-[12px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
        <span>套餐内积分额度：{periodCredits ?? 0}</span>
        <span className="mx-4 h-2 w-px" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
        <span>{periodExpiresAt ? `${periodExpiresAt.slice(0, 10).replace(/-/g, '/')} 重置` : ''}</span>
      </div>
    </div>
  </div>
  );
};

/** 水印设置规则卡 #169:35：584x412 / #2D2D2D 2% / 圆角 8（Figma 169-32）
    卡内：标题 (24,24) / 正文 (24,52) 宽536 / 开关行 (24,272) / 确认说明 (24,300) 宽536 */
const WatermarkPanel: React.FC = () => {
  // 「去AI水印」开关 #169:161：开启态为白胶囊 32x16 圆角 8 + #1D1D1D 滑块居右（设计稿切图即此态）；
  // 关闭态按同构推导（胶囊白 25% / 白滑块居左）。开关状态暂为本地演示，待后端水印设置字段接入
  const [removeWatermark, setRemoveWatermark] = useState(true);
  return (
    <div
      className="absolute left-6 top-16 h-[412px] w-[584px] rounded-lg px-6"
      style={{ backgroundColor: 'rgba(45,45,45,0.02)' }}
    >
      {/* 卡内标题 #169:150：16px Semibold @(24,24) */}
      <h4 className="pt-6 text-[16px] font-semibold leading-[16px] text-white">AI生成内容水印管理规则</h4>
      {/* 规则正文 #169:151：12px Light / 行高 24 / 两端对齐（两段以换行分隔） */}
      <p
        className="mt-3 w-[536px] text-[12px] font-light leading-6 text-white"
        style={{ textAlign: 'justify', whiteSpace: 'pre-line' }}
      >
        {WATERMARK_RULES_TEXT}
      </p>
      {/* 开关行 #169:161 + 「去AI水印」#169:158：开关 32x16，文字 14px Medium（卡内 x=64） */}
      <button
        onClick={() => setRemoveWatermark((v) => !v)}
        role="switch"
        aria-checked={removeWatermark}
        aria-label="去AI水印"
        className="absolute left-6 top-[272px] flex items-center"
      >
        <span
          className="relative block h-4 w-8 shrink-0 rounded-full transition-colors duration-200"
          style={{ backgroundColor: removeWatermark ? '#FFFFFF' : 'rgba(255,255,255,0.25)' }}
        >
          <span
            className="absolute top-1 h-3 w-3 rounded-full transition-all duration-200"
            style={{ left: removeWatermark ? 18 : 2, backgroundColor: removeWatermark ? '#1D1D1D' : '#FFFFFF' }}
          />
        </span>
        <span className="ml-2 text-[14px] font-medium leading-[14px] text-white">去AI水印</span>
      </button>
      {/* 底部确认说明 #169:157：12px Thin / 行高 24 / 两端对齐 */}
      <p
        className="absolute left-6 top-[300px] w-[536px] text-[12px] font-extralight leading-6 text-white"
        style={{ textAlign: 'justify' }}
      >
        打开开关即代表您已确认充分了解上述情况并同意，后续您可以通过"头像" - "AI水印设置"里面修改水印设置
      </p>
    </div>
  );
};

interface PersonalInfoModalProps {
  onClose: () => void;
  /** 初始页签：「个人中心」入口默认 profile，「水印设置」入口传 watermark（Figma 169-32） */
  initialTab?: AccountTab;
}

export const PersonalInfoModal: React.FC<PersonalInfoModalProps> = ({ onClose, initialTab = 'profile' }) => {
  const { user, logout } = useAuthStore();
  // 当前页签：左菜单在「个人信息 / 水印设置」两个已实现页面间切换
  const [activeTab, setActiveTab] = useState<AccountTab>(initialTab);
  // 菜单页签切换：仅已实现的两页可切换，其余项点击不响应
  const handleMenuClick = (key: string) => {
    if (key === 'profile' || key === 'watermark') setActiveTab(key);
  };
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 复制 UID（与 UserMenu 下拉同逻辑：clipboard API 优先，回退 execCommand）
  const handleCopyUid = async () => {
    if (!user) return;
    const uid = String(user.id);
    try {
      await navigator.clipboard.writeText(uid);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = uid;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  // ESC 关闭
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, [onClose]);

  // 仅登录态有内容可渲染（入口在 UserMenu 已登录分支内，此处兜底）
  if (!user) return null;

  const points = user.points ?? 0;
  // 会员开通态：后端 membershipTier（plan 非 free 且资格未过期）非 none 即已开通
  const hasMembership = (user.membershipTier ?? 'none') !== 'none';
  const maskedPhone = user.phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');

  return createPortal(
    /* 遮罩 #168:224：全屏 #121212 blur(8px) 65%，点击关闭 */
    <div
      className="fixed inset-0 z-[250] animate-in fade-in duration-150"
      style={{ backgroundColor: 'rgba(18,18,18,0.65)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      {/* 居中容器：弹窗按设计稿 800x586 原始尺寸常规渲染（无缩放） */}
      <div className="flex h-full w-full items-center justify-center">
        {/* 弹窗 #168:240：800x586 / #1D1D1D / 边框 1px #2D2D2D / 圆角 16（overflow-hidden 裁出左菜单左圆角） */}
        <div
          className="flex overflow-hidden rounded-2xl animate-in fade-in zoom-in-95 duration-150"
          style={{
            width: 800,
            height: 586,
            backgroundColor: '#1D1D1D',
            border: '1px solid #2D2D2D',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ═══ 左菜单栏：168 宽 / #222222（#168:241）═══ */}
          <aside className="flex h-full shrink-0 flex-col pt-6" style={{ width: 168, backgroundColor: '#222222' }}>
            {/* 「账户中心」标题：16px Semibold */}
            <div className="mx-4 shrink-0 text-[16px] font-semibold leading-[16px] text-white">账户中心</div>

            <GroupLabel text="我的" />
            {MY_GROUP.map((item) => (
              <MenuItem key={item.key} item={item} active={item.key === activeTab} onClick={() => handleMenuClick(item.key)} />
            ))}

            <GroupLabel text="系统" />
            {SYSTEM_GROUP.map((item) => (
              <MenuItem key={item.key} item={item} active={item.key === activeTab} onClick={() => handleMenuClick(item.key)} />
            ))}

            {/* 退出登录：#F53F3F 65%，点击登出并关闭弹窗 */}
            <button
              onClick={() => {
                onClose();
                logout();
              }}
              className="mx-2 mt-auto mb-5 flex h-10 shrink-0 items-center rounded-lg transition-colors hover:bg-white/5"
            >
              <img src="/icons/user-menu/logout.svg" alt="" width={16} height={16} className="ml-4 shrink-0" />
              <span
                className="ml-2 text-[14px] leading-[14px]"
                style={{ color: 'rgba(245,63,63,0.65)' }}
              >
                退出登录
              </span>
            </button>
          </aside>

          {/* ═══ 右内容区：632 宽（800-168）═══ */}
          <main className="relative h-full flex-1">
            {/* 页签标题：16px Semibold @(24,24)，随页签切换 */}
            <h3 className="absolute left-6 top-6 text-[16px] font-semibold leading-[16px] text-white">
              {activeTab === 'watermark' ? '水印设置' : '个人信息'}
            </h3>

            {/* ── 水印设置页（169-32）── */}
            {activeTab === 'watermark' && <WatermarkPanel />}

            {/* ── 个人信息页内容 ── */}
            {/* 用户卡底框 #168:693：584x112 白 2% / 圆角 8，与下方会员卡左对齐同宽（内容 @(48,88) 四周留白 24） */}
            {activeTab === 'profile' && (
            <div className="absolute left-6 top-16 h-[112px] w-[584px] rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }} />
            )}
            {/* 用户卡内容 #168:673：410x64 @(48,88)，头像 64 圆 #0092FF 缩进 24 对齐 */}
            {activeTab === 'profile' && (
            <div className="absolute left-12 top-[88px] flex items-center" style={{ width: 410, height: 64 }}>
              {user.avatar ? (
                <img src={user.avatar} alt={user.nickname} className="h-16 w-16 shrink-0 rounded-full object-cover" />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-white" style={{ backgroundColor: '#0092FF' }}>
                  <UserIcon size={28} />
                </div>
              )}
              <div className="ml-4 flex min-w-0 flex-col gap-[10px]">
                {/* 昵称行：18px Medium #8BCDFF + 编辑 icon */}
                <div className="flex items-center gap-2">
                  <span className="max-w-[200px] truncate text-[18px] font-medium leading-[18px]" style={{ color: '#8BCDFF' }}>
                    {user.nickname}
                  </span>
                  <button title="编辑昵称" className="shrink-0 transition-opacity hover:opacity-80">
                    <img src="/icons/user-center/edit.svg" alt="编辑昵称" width={16} height={16} />
                  </button>
                </div>
                {/* 账号行：掩码手机 | 分隔线 | UID + 复制 */}
                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap text-[14px] leading-[14px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {maskedPhone}
                  </span>
                  <span className="h-2 w-px shrink-0" style={{ backgroundColor: 'rgba(217,217,217,0.45)' }} />
                  <span className="whitespace-nowrap text-[14px] leading-[14px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
                    UID：{user.id}
                  </span>
                  <button
                    onClick={handleCopyUid}
                    title={copied ? '已复制' : '复制UID'}
                    className="flex shrink-0 items-center transition-opacity hover:opacity-80"
                  >
                    {copied && <span className="mr-1 whitespace-nowrap text-[12px] leading-[12px] text-white">已复制</span>}
                    <img src="/icons/user-menu/copy.svg" alt="复制UID" width={16} height={16} className={copied ? 'opacity-100' : 'opacity-65'} />
                  </button>
                </div>
              </div>
            </div>
            )}

            {/* 会员卡区 @(24,188)：按会员状态渲染未开通（#168:718）/ 已开通（#168:719）二选一 */}
            {activeTab === 'profile' && (
            <div className="absolute left-6 top-[188px]">
              {hasMembership
                ? (
                  <ActiveMemberCard
                    points={points}
                    expiresAt={user.subscriptionExpiresAt}
                    tierName={TIER_SHORT_NAME[user.membershipTier ?? 'none']}
                    periodCredits={user.currentPeriodCredits}
                    periodExpiresAt={user.currentPeriodExpiresAt}
                  />
                )
                : <InactiveMemberCard points={points} />}
            </div>
            )}
          </main>
        </div>
      </div>
    </div>,
    document.body
  );
};
